import { useMutation } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { useState } from "react";

import { IMAGE_TYPE_REFUSAL, uploadBoxImage } from "./imageUpload";
import { useTRPC } from "./trpc";

/**
 * The builder's box image: uploaded as soon as it is picked (before the box exists), its public
 * URL then saved with the box. "" = no image.
 */
export function useBoxImage(initialUrl: string) {
  const trpc = useTRPC();
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState<string | null>(null);
  const start = useMutation(trpc.boxes.startImageUpload.mutationOptions());
  const finish = useMutation(trpc.boxes.finishImageUpload.mutationOptions());

  async function pick(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      setUrl(
        await uploadBoxImage(file, start.mutateAsync, (uploadKey) =>
          finish.mutateAsync({ uploadKey }),
        ),
      );
    } catch (caught) {
      // A refused call is already a toast (queryClient's MutationCache); the checks before and
      // the PUT between the two calls are shown on the drop zone.
      if (caught instanceof TRPCClientError) return;
      setError(caught instanceof Error ? caught.message : "The image could not be uploaded.");
    }
  }

  return {
    url,
    error,
    uploading: start.isPending || finish.isPending,
    pick,
    reject: () => setError(IMAGE_TYPE_REFUSAL),
    clear: () => {
      setUrl("");
      setError(null);
    },
  };
}

export type BoxImage = ReturnType<typeof useBoxImage>;
